import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

interface InAppCameraProps {
  onCapture: (blob: Blob) => void;
  onClose: () => void;
}

export const InAppCamera: React.FC<InAppCameraProps> = ({ onCapture, onClose }) => {
  const videoRef  = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [ready,    setReady]    = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [error,    setError]    = useState<string | null>(null);

  // Manage the camera stream entirely inside the effect — no stream in React state
  // to avoid the re-render timing window where srcObject gets lost.
  useEffect(() => {
    let active = true;

    const start = async () => {
      setReady(false);
      setError(null);

      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Camera is not supported in this browser.');
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false
        });

        if (!active) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            if (active) setReady(true);
          };
        }

        // Expose stream to cleanup
        return stream;
      } catch (err: any) {
        if (!active) return;
        setError(
          err.name === 'NotAllowedError'
            ? 'Camera access was denied. Please allow camera access in your browser settings.'
            : 'Could not access camera. Please try again.'
        );
      }
    };

    // Start and hold the stream promise for cleanup
    const streamPromise = start();

    return () => {
      active = false;
      streamPromise.then(stream => {
        if (stream) stream.getTracks().forEach(t => t.stop());
      });
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [facingMode]);

  const handleCapture = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !ready) return;

    setCapturing(true);
    canvas.width  = video.videoWidth  || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) { setCapturing(false); return; }
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(blob => {
      setCapturing(false);
      if (blob) onCapture(blob);
    }, 'image/jpeg', 0.92);
  };

  const handleClose = () => {
    // Stream is cleaned up by the useEffect return
    onClose();
  };

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      backgroundColor: '#000', display: 'flex', flexDirection: 'column'
    }}>
      {/* Viewfinder */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {error ? (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            color: '#fff', padding: '32px', textAlign: 'center', gap: '16px'
          }}>
            <span className="material-icons" style={{ fontSize: '48px', opacity: 0.5 }}>no_photography</span>
            <p style={{ margin: 0, opacity: 0.8 }}>{error}</p>
            <button className="btn btn-contained" onClick={handleClose}>Close</button>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
                opacity: ready ? 1 : 0,
                transition: 'opacity 0.3s'
              }}
            />
            {!ready && (
              <div style={{
                position: 'absolute', inset: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center', color: '#fff'
              }}>
                <span className="material-icons" style={{ fontSize: '32px', opacity: 0.5 }}>camera</span>
              </div>
            )}
          </>
        )}

        {/* Flip button */}
        {!error && (
          <button
            onClick={() => setFacingMode(m => m === 'environment' ? 'user' : 'environment')}
            style={{
              position: 'absolute', top: '16px', right: '16px',
              background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%',
              width: '44px', height: '44px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff'
            }}
          >
            <span className="material-icons">flip_camera_ios</span>
          </button>
        )}
      </div>

      {/* Controls */}
      <div style={{
        padding: '24px 32px 40px', display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: '12px', background: '#111'
      }}>
        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)' }}>
          🔒 Photos taken here are never saved to your device
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: '48px' }}>
          <button
            onClick={handleClose}
            style={{
              background: 'none', border: '2px solid rgba(255,255,255,0.4)',
              borderRadius: '50%', width: '48px', height: '48px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff'
            }}
          >
            <span className="material-icons">close</span>
          </button>

          <button
            onClick={handleCapture}
            disabled={!ready || capturing || !!error}
            style={{
              background: (!ready || capturing) ? 'rgba(255,255,255,0.5)' : '#fff',
              border: '4px solid rgba(255,255,255,0.3)', borderRadius: '50%',
              width: '72px', height: '72px',
              cursor: ready && !capturing && !error ? 'pointer' : 'not-allowed',
              transition: 'transform 0.1s',
              transform: capturing ? 'scale(0.92)' : 'scale(1)'
            }}
          />

          <div style={{ width: '48px' }} />
        </div>
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>,
    document.body
  );
};
